import React, {FC, useMemo} from 'react';

import {HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {buildCustomViewCounterKey} from '../../../helpers/counterHelpers';
import {computeFlatVisualCustomViews, VisualCustomView} from '../../../helpers/models/customViewHelpers';
import {DelegationModel} from '../../../models/api/delegationModel';
import {VisualWorkspaceTag} from '../../../models/api/visualWorkspaceModel';
import {
  WorkspaceDropReorderTagHandler,
  WorkspaceTagDropNestHandler,
} from '../../../store/data/workspace/reorder/types';
import {computeVisualWorkspaceRecentlyViewedMap} from '../../../store/data/workspace/visualWorkspace/visualWorkspaceHelpers';
import {InboxesPanelId} from '../../../store/panels/panelStateBase';
import {WorkspaceCustomViewSubtreeContainer} from './workspaceCustomViewSubtreeContainer';

/*
 * Props.
 */

interface WorkspaceCustomViewsProps {
  panelId: InboxesPanelId;
  visualCustomViews: ReadonlyArray<VisualCustomView>;
  delegation?: DelegationModel;
  /** Is the workspace in edit mode. */
  isEditingWorkspace?: boolean;
  /**
   * The currently selected tag IDs pertaining to the <WorkspacesEditor />.
   * This is used to determine whether or not the checkbox should be displayed as checked.
   */
  visualWorkspaceUserTags?: ReadonlyArray<VisualWorkspaceTag>;
  /**
   * An optional click handler for the individually displayed VisualCustomView's.
   * Note that they already link to the custom view when not editing the workspace.
   */
  onCustomViewClick?: HandlerOf<VisualCustomView>;
  onCustomViewToggleExpandInEditor?: HandlerOf<VisualCustomView>;
  shouldAlphabetizeWorkspace: boolean;
  onDropReorderCustomView: WorkspaceDropReorderTagHandler;
  onDropNestCustomView: WorkspaceTagDropNestHandler;
}

/*
 * Component.
 */

export const WorkspaceCustomViews: FC<WorkspaceCustomViewsProps> = (props) => {
  const {
    panelId,
    visualCustomViews,
    delegation,
    isEditingWorkspace,
    onCustomViewClick,
    onCustomViewToggleExpandInEditor,
    onDropReorderCustomView,
    onDropNestCustomView,
    shouldAlphabetizeWorkspace,
    visualWorkspaceUserTags,
  } = props;

  const flatVisualCustomViews = useMemo(
    () => computeFlatVisualCustomViews(visualCustomViews),
    [visualCustomViews],
  );

  const previousParentTagChildrenMap = useMemo(
    () => computeParentTagChildrenMap(flatVisualCustomViews),
    [flatVisualCustomViews],
  );

  const visualCustomViewsWithHiddenChildren = useMemo(
    () =>
      computeVisualCustomViewsWithHiddenChildren(
        visualCustomViews,
        previousParentTagChildrenMap,
        isEditingWorkspace,
      ),
    [visualCustomViews, previousParentTagChildrenMap, isEditingWorkspace],
  );

  const recentlyViewedMap = visualWorkspaceUserTags
    ? computeVisualWorkspaceRecentlyViewedMap(visualWorkspaceUserTags)
    : undefined;

  // In edit mode, there is no need to render the hidden children because we don't do any
  // optimistic updating.
  const visualCustomViewsToRender = isEditingWorkspace
    ? visualCustomViews
    : visualCustomViewsWithHiddenChildren;
  const lastIndex = visualCustomViewsToRender.length - 1;
  return (
    <>
      {visualCustomViewsToRender.map((visualCustomView, index) => {
        const counterKey = !isEditingWorkspace
          ? buildCustomViewCounterKey(visualCustomView.uid, visualCustomView.type)
          : undefined;

        return (
          <WorkspaceCustomViewSubtreeContainer
            key={visualCustomView.id}
            panelId={panelId}
            customView={visualCustomView}
            counterKey={counterKey}
            counterDelegationId={delegation && delegation.id}
            canDrag={!delegation}
            isLastItem={index === lastIndex}
            onDropReorderCustomView={onDropReorderCustomView}
            onDropNestCustomView={onDropNestCustomView}
            onClick={onCustomViewClick}
            isEditingWorkspace={isEditingWorkspace}
            recentlyViewedMap={recentlyViewedMap}
            onToggleExpandInEditor={onCustomViewToggleExpandInEditor}
            shouldAlphabetizeWorkspace={shouldAlphabetizeWorkspace}
          />
        );
      })}
    </>
  );
};

/*
 * Helpers.
 */

/** Compute a map of parentTagId to their respective children, an array of VisualCustomView */
function computeParentTagChildrenMap(customViews: ReadonlyArray<VisualCustomView>) {
  return customViews.reduce((currentMap, customView) => {
    if (!customView.tag.previousParentTagId) {
      return currentMap;
    }

    const currentChildren = currentMap.get(customView.tag.previousParentTagId) || [];
    currentMap.set(customView.tag.previousParentTagId, [...currentChildren, customView]);

    return currentMap;
  }, new Map<number, ReadonlyArray<VisualCustomView>>());
}

/** Compute which children are no longer in the current state, and return them with an isHidden property. */
function computeHiddenChildren(
  customView: VisualCustomView,
  previousChildren: ReadonlyArray<VisualCustomView>,
) {
  return previousChildren.reduce((currentChildren, previousChild) => {
    if (customView.children.find((child) => child.id === previousChild.id)) {
      return currentChildren;
    }

    // If this VisualCustomView is missing a child which existed in the previous state, we include it as a hidden child.
    const hiddenPreviousChild = {...previousChild, isHidden: true};

    return [...currentChildren, hiddenPreviousChild];
  }, [] as ReadonlyArray<VisualCustomView>);
}

/** Compute the children of a CustomView given its previousChildren, to include previous children as hidden entries. */
function computeChildren(
  customView: VisualCustomView,
  previousChildren: ReadonlyArray<VisualCustomView> | undefined,
) {
  if (!previousChildren || !previousChildren.length) {
    // If this VisualCustomView had no children in the previous state, there is no need to compute hidden children.
    return customView.children;
  }

  // Determine which children existed in the previous state but not the current one.
  const hiddenChildren = computeHiddenChildren(customView, previousChildren);

  return [...customView.children, ...hiddenChildren];
}

/** Recreate the VisualCustomView items and their nested children, while inserting hidden entries for tags which have been moved. */
function computeVisualCustomViewsWithHiddenChildren(
  customViews: ReadonlyArray<VisualCustomView>,
  previousParentTagChildrenMap: Map<number, ReadonlyArray<VisualCustomView>>,
  isEditingWorkspace: boolean | undefined,
): ReadonlyArray<VisualCustomView> {
  if (isEditingWorkspace) {
    return [];
  }

  return customViews.reduce((currentCustomViews, customView) => {
    const previousChildren = previousParentTagChildrenMap.get(customView.id);

    // Compute the new children of a CustomView with its hidden children attached.
    const children = computeChildren(customView, previousChildren);

    // Recursively add hidden children to each VisualCustomView child, to account for deeply nested tags.
    const childrenWithHiddenChildren = computeVisualCustomViewsWithHiddenChildren(
      children,
      previousParentTagChildrenMap,
      isEditingWorkspace,
    );

    const customViewWithHiddenChildren = {
      ...customView,
      children: childrenWithHiddenChildren,
    };

    return [...currentCustomViews, customViewWithHiddenChildren];
  }, [] as ReadonlyArray<VisualCustomView>);
}
